/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { verificationClient } from '../../../../services/verificationClient';
import { PolicyRequirementCategory } from '../../../../types/VerificationTypes';


/**
 * Read-only requirement categories registry.
 *
 * Mutations happen via Django seed migrations, not the UI.
 */
const RequirementCategoriesSection: React.FC = () => {
  const [rows, setRows] = useState<PolicyRequirementCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await verificationClient.listRequirementCategories());
    } catch (err) {
      apiResponseHandler.handleApiError(err);
      setError('Could not load requirement categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-lg">
        <Loader2 className="animate-spin text-secondary-text-label" size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        variant="bordered"
        size="sm"
        icon="alert"
        title="Couldn’t load requirement categories"
        description={error}
        actions={<Button onClick={load}>Try again</Button>}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        variant="bordered"
        size="sm"
        icon="folder"
        title="No categories yet"
        description="Requirement categories are created via seed migrations."
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Code</TableHead>
          <TableHead>Sort</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.code}>
            <TableCell>{r.name}</TableCell>
            <TableCell>
              <code className="text-xs">{r.code}</code>
            </TableCell>
            <TableCell>{r.sort_order}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default RequirementCategoriesSection;
